import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, App, Button, Modal, Radio, Select, Tag, Tooltip, Typography } from 'antd';

import { CLOSE, PLUS } from '../icons';
import ToggleRow from '../common/ToggleRow';
import UserRef from '../users/UserRef';
import { userLabel } from '../users/UserChip';
import UserSearchSelect from '../users/UserSearchSelect';
import { getSharing } from '../../services/sharing';
import { deleteElementGrant, putElementGrant } from '../../services/elementGrants';
import { isGlobal } from '../../utils/collectionKinds';
import { isSubCollection, parentOf } from '../../utils/collectionFamily';
import { normalizeId } from '../../utils/ids';
import {
	FOLLOWERS_LABEL,
	KEYS_LABEL,
	LEVELS,
	LEVEL_LABELS,
	PUBLIC_LABEL,
	ROLE_AUDIENCES,
	ROLE_LABELS,
	SHARE_LABEL,
	aboveGrant,
	aboveLevelFor,
	audienceKey,
	conflictLink,
	conflictParts,
	conflictsOf,
	effectiveWords,
	exceedsAbove,
	governingGrant,
	groupLevelFor,
	groupLevelsOf,
	levelWords,
	inheritedFor,
	isMissingAudience,
	sameStatement,
	statementFromRows,
} from '../../utils/elementAccess';

const { Text } = Typography;

/**
 * WHO CAN SEE OR CHANGE ONE ELEMENT — a manager's dialog over one element's
 * rule in one collection (KPR-509; keepr-api docs/SCHEMA.md § Element
 * grants; owner rulings e01–e03, e06; plan § Web surfaces).
 *
 * A self-contained dialog with its own Save, never part of the card's draft:
 * a rule has its own endpoint and applies the moment it is saved, like the
 * Sharing tab — and the card page opens it from the card's home too, where a
 * manager of a collection may set rules on a card they cannot edit (a global
 * card opened from their collection).
 *
 * One row per audience: the three roles always; followers when the
 * collection is published, public form visitors when its intake forms are
 * on (either also whenever a rule names them); every group or person a rule
 * names, and "Add a person or group" for more. Each row is a three-way
 * choice Hidden / See only / Edit — a radio group drawn as buttons rather
 * than antd's Segmented, because Segmented always shows one option chosen
 * and a row with no rule must show none: that is "Follows the card". Beside
 * it, what the row comes to (utils/elementAccess effectiveWords): rules only
 * narrow, managers always read, viewers and followers never change items, a
 * group built from items only lowers.
 *
 * Save sends the whole statement (a PUT replaces it). With nothing chosen:
 * the card's own rule is deleted — unless a card above this one has a rule
 * for the element, when an empty rule is SAVED, because a row of this card's
 * own, even an empty one, is what stops the ancestor's applying here
 * (keepr-api § Element grants, "Descendant cards inherit").
 *
 * The server decides. A refusal shows its own words; 409
 * `element_rule_conflicts` lists every setup that would print the value,
 * each linked to where it is fixed, and saves nothing (KPR-508).
 *
 * In a SUB-COLLECTION, what the collection above gives (KPR-623; keepr-api §
 * What the collection above gives — `inherited`, from the GET): a rule here
 * may only narrow it (409 `element_rule_widens`), so each of the five stated
 * audiences' rows shows the level the parent's rule gives it, read-only, and
 * says when the row's own level is more than that — before Save, not after.
 * "Start from {parent}'s rule" fills those rows with it (and "Only in keepr"
 * when the parent keeps it from keys); a person or group row is left as it
 * is, for the server to judge. The levels never name the parent's people or
 * groups: the API does not send them.
 */
export default function ElementAccessModal({
	open,
	onClose,
	onSaved,
	collectionId,
	collection = null,
	// The card whose page this is, and its chain parent-first (ending with it).
	cardId,
	cardName = '',
	chainIds = [],
	cardNamesById = null,
	// The element as the merged definition carries it: `id`, `name`, `label`.
	// For a COLUMN of a repeating group (KPR-688), the column — and `group`,
	// the group it is a column of.
	element,
	group = null,
	grants = [],
	// What the collection above gives each element of this card (KPR-623):
	// the GET's `inherited`, or null when the API said nothing about it.
	inherited = null,
}) {
	const { message } = App.useApp();
	const elementId = `${element?.id || ''}`;
	const label = element?.label?.singular || element?.name || 'this element';
	// A COLUMN's rule (KPR-688) narrows its GROUP's: the column follows the
	// group until a level is chosen here, and never gives anybody more than the
	// group does (the server holds it to that, whatever is saved).
	const groupLabel = group ? (group.label?.singular || group.name || 'its group') : null;
	const subject = group ? `${label} in ${groupLabel}` : label;
	const followsWhat = group ? groupLabel : 'the card';
	const groupGoverning = useMemo(
		() => (group?.id ? governingGrant(grants, chainIds, `${group.id}`) : null),
		[grants, chainIds, group]
	);
	const groupLevels = useMemo(() => (group ? groupLevelsOf(groupGoverning?.grant) : null), [group, groupGoverning]);

	const governing = useMemo(() => governingGrant(grants, chainIds, elementId), [grants, chainIds, elementId]);
	const own = governing?.own ? governing.grant : null;
	// What the table starts from: the governing ROW, by identity — not the
	// chain that ordered it, which can arrive while the dialog is open and
	// must not wipe a manager's edits by re-deriving the same row.
	const governingRow = governing?.grant || null;
	// The rule a card above this one has: what applies once this card's own
	// is removed (and the reason an empty statement is saved, not deleted).
	const above = useMemo(() => aboveGrant(grants, chainIds, elementId), [grants, chainIds, elementId]);
	const aboveName = above ? (cardNamesById?.[normalizeId(above.grant.card_id)] || 'a card above this one') : null;

	const published = isGlobal(collection);
	// A sub-collection's items are also governed by a rule its PARENT
	// collection sets, which this dialog cannot read (the GET answers this
	// collection's rows; a manager here may not manage the parent). So it
	// says one may exist, and an emptied rule is SAVED rather than deleted:
	// an own row, even an empty one, is what makes "follows the card" true
	// here whatever the parent says (the KPR-509 review's finding 3).
	const sub = isSubCollection(collection);
	const parentName = parentOf(collection)?.name || 'the collection above';
	const intakeOn = collection?.allowIntakeLinks === true;
	// The API told us what the collection above gives (an array, even an
	// empty one); null leaves the older words, which only say it may.
	const aboveKnown = sub && Array.isArray(inherited);
	const fromAbove = useMemo(
		() => (aboveKnown ? inheritedFor(inherited, cardId, elementId) : null),
		[aboveKnown, inherited, cardId, elementId]
	);
	const aboveLevels = fromAbove && !fromAbove.unknown ? fromAbove : null;
	// "Clinic’s rule" — or, with no name to hand, "the rule above" (and its
	// twin at the start of a sentence; a collection's name keeps its own case).
	const aboveRule = parentOf(collection)?.name ? `${parentName}’s rule` : 'the rule above';
	const AboveRule = parentOf(collection)?.name ? aboveRule : 'The rule above';

	const initial = useMemo(() => {
		const rules = Array.isArray(governingRow?.rules) ? governingRow.rules : [];
		const byKey = new Map(rules.map(rule => [audienceKey(rule?.audience), rule]));
		const rows = ROLE_AUDIENCES.map(role => {
			const key = `role:${role}`;
			return { key, audience: { type: 'role', role }, level: byKey.get(key)?.level || null, fixed: true };
		});
		// The collection above holding followers or the form back is shown
		// too, so a manager sees what a rule here must keep.
		const heldAbove = type => {
			const level = aboveLevelFor(aboveLevels, { type });
			return type === 'public' ? (level === 'see' || level === 'hidden') : level === 'hidden';
		};
		if (published || byKey.has('followers') || heldAbove('followers')) rows.push({ key: 'followers', audience: { type: 'followers' }, level: byKey.get('followers')?.level || null, fixed: true });
		if (intakeOn || byKey.has('public') || heldAbove('public')) rows.push({ key: 'public', audience: { type: 'public' }, level: byKey.get('public')?.level || null, fixed: true });
		rules.forEach(rule => {
			const type = rule?.audience?.type;
			if (type !== 'group' && type !== 'user') return;
			const key = audienceKey(rule.audience);
			if (key) rows.push({ key, audience: rule.audience, level: rule.level || null, fixed: false });
		});
		return { rows, keeprOnly: governingRow?.keeprOnly === true };
	}, [governingRow, published, intakeOn, aboveLevels]);

	const [rows, setRows] = useState(initial.rows);
	const [keeprOnly, setKeeprOnly] = useState(initial.keeprOnly);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState(null);
	const [conflicts, setConflicts] = useState(null);
	const [adding, setAdding] = useState(false);

	useEffect(() => {
		if (!open) return;
		setRows(initial.rows);
		setKeeprOnly(initial.keeprOnly);
		setError(null);
		setConflicts(null);
		setAdding(false);
	}, [open, initial]);

	// The collection's groups (and, on a sub-collection, the groups above it
	// a rule may name) — what "Add a person or group" offers. Read when the
	// dialog opens; a failure leaves the person picker, which needs none.
	const [sharing, setSharing] = useState(null);
	useEffect(() => {
		if (!open || !collectionId) return undefined;
		let live = true;
		getSharing(collectionId)
			.then(data => { if (live) setSharing(data || null); })
			.catch(() => { if (live) setSharing(null); });
		return () => { live = false; };
	}, [open, collectionId]);

	const statement = useMemo(() => statementFromRows(rows, keeprOnly), [rows, keeprOnly]);
	const initialStatement = useMemo(() => statementFromRows(initial.rows, initial.keeprOnly), [initial]);
	// Dirty when the table differs from what was shown — or when what was
	// shown is an ANCESTOR's rule the manager now wants as this card's own,
	// which "Save" alone cannot tell: that takes a change.
	const dirty = !sameStatement(statement, initialStatement)
		|| rows.some(row => isMissingAudience(row.audience)) !== initial.rows.some(row => isMissingAudience(row.audience));
	const missingCount = rows.filter(row => isMissingAudience(row.audience)).length;

	const setLevel = (key, level) => setRows(prev => prev.map(row => (row.key === key ? { ...row, level } : row)));

	// "Start from {parent}'s rule": the five stated audiences at the levels
	// the collection above gives — exactly the statement the server accepts
	// (keepr-api § What the collection above gives). Followers and the form
	// get a row only where a level of their own says more than leaving them
	// out would (followers follow the viewers; the form follows the form).
	const startFromAbove = () => {
		if (!aboveLevels) return;
		setRows((prev) => {
			let next = prev.map(row => (row.fixed && aboveLevelFor(aboveLevels, row.audience) ? { ...row, level: aboveLevelFor(aboveLevels, row.audience) } : row));
			const viewers = aboveLevelFor(aboveLevels, { type: 'role', role: 'read' });
			const followers = aboveLevelFor(aboveLevels, { type: 'followers' });
			if (followers && !next.some(row => row.key === 'followers') && followers !== (viewers === 'edit' ? 'see' : viewers)) {
				const at = next.findIndex(row => row.key === 'public');
				const added = { key: 'followers', audience: { type: 'followers' }, level: followers, fixed: true };
				next = at === -1 ? [...next, added] : [...next.slice(0, at), added, ...next.slice(at)];
			}
			const visitors = aboveLevelFor(aboveLevels, { type: 'public' });
			if (visitors && visitors !== 'edit' && !next.some(row => row.key === 'public')) {
				next = [...next, { key: 'public', audience: { type: 'public' }, level: visitors, fixed: true }];
			}
			return next;
		});
		if (aboveLevels.keeprOnly) setKeeprOnly(true);
	};
	const clearRow = (row) => {
		if (row.fixed) setLevel(row.key, null);
		else setRows(prev => prev.filter(other => other.key !== row.key));
	};

	const groupOptions = useMemo(() => {
		const taken = new Set(rows.map(row => row.key));
		const own = Array.isArray(sharing?.groups) ? sharing.groups : [];
		const inherited = Array.isArray(sharing?.inheritedGroups) ? sharing.inheritedGroups : [];
		return [...own, ...inherited]
			.filter(group => group && !taken.has(`group:${normalizeId(group._id)}`))
			.map(group => ({
				value: normalizeId(group._id),
				label: group.kind === 'dynamic' ? `${group.name} · built from items` : group.name,
				group,
			}));
	}, [sharing, rows]);
	const takenUserIds = useMemo(
		() => rows.filter(row => row.audience?.type === 'user').map(row => normalizeId(row.audience.id)),
		[rows]
	);

	const addGroup = (groupId) => {
		const group = groupOptions.find(option => option.value === groupId)?.group;
		if (!group) return;
		const audience = { type: 'group', id: normalizeId(group._id), name: group.name, kind: group.kind || 'static' };
		setRows(prev => [...prev, { key: audienceKey(audience), audience, level: null, fixed: false }]);
		setAdding(false);
	};
	const addUser = (userId) => {
		const id = normalizeId(userId);
		if (!id || takenUserIds.includes(id)) return;
		const audience = { type: 'user', id };
		setRows(prev => [...prev, { key: audienceKey(audience), audience, level: null, fixed: false }]);
		setAdding(false);
	};

	const finish = (text, result) => {
		message.success(text);
		const paused = Number(result?.pausedNotifications) || 0;
		if (paused) {
			message.info(paused === 1
				? 'One person’s own notification would have shown this element, so it was paused. They can turn it back on.'
				: `${paused} people’s own notifications would have shown this element, so they were paused. Each can be turned back on.`);
		}
		onSaved?.();
		onClose?.();
	};

	const refusal = (err, fallback) => {
		const list = conflictsOf(err);
		if (list) {
			setConflicts(list);
			setError(null);
			return;
		}
		const code = err?.response?.data?.code;
		setConflicts(null);
		if (code === 'element_grants_off') setError('Element access is not switched on yet, so nothing was saved.');
		else if (code === 'concurrent_change') setError('Someone else saved this element’s access at the same moment. Close this and open it again to see theirs.');
		else setError(err?.response?.data?.message || fallback);
	};

	const save = async () => {
		if (!elementId || !cardId || !collectionId) return;
		setSaving(true);
		setError(null);
		setConflicts(null);
		try {
			const empty = !statement.rules.length && !statement.keeprOnly;
			if (empty && !above && !sub) {
				// Nothing to say and nothing above to stop: back to the card.
				const result = own ? await deleteElementGrant(collectionId, cardId, elementId) : null;
				finish(`${label} follows ${followsWhat} again.`, result);
			} else {
				const result = await putElementGrant(collectionId, cardId, elementId, statement);
				finish(`Saved who can see or change ${label}.`, result);
			}
		} catch (err) {
			refusal(err, 'Could not save. Nothing was changed.');
		} finally {
			setSaving(false);
		}
	};

	const removeOwn = async () => {
		if (!own) return;
		setSaving(true);
		setError(null);
		setConflicts(null);
		try {
			const result = await deleteElementGrant(collectionId, cardId, elementId);
			finish(above
				? `${label} follows ${aboveName}’s rule again.`
				: (sub ? `This collection’s rule for ${label} is removed. A rule ${parentName} sets applies, if it has one.` : `${label} follows ${followsWhat} again.`), result);
		} catch (err) {
			refusal(err, 'Could not remove the rule. Nothing was changed.');
		} finally {
			setSaving(false);
		}
	};

	const footer = (
		<div className="element-access__footer">
			{own ? (
				<Button onClick={removeOwn} disabled={saving}>
					{above ? `Use ${aboveName}’s rule` : (sub ? 'Remove this collection’s rule' : `Follow ${followsWhat}`)}
				</Button>
			) : <span />}
			<span className="element-access__footer-end">
				<Button onClick={onClose} disabled={saving}>Cancel</Button>
				<Button type="primary" onClick={save} loading={saving} disabled={!dirty && !(missingCount && own)}>
					Save
				</Button>
			</span>
		</div>
	);

	return (
		<Modal
			open={open}
			onCancel={saving ? undefined : onClose}
			title={`Who can see or change ${subject}`}
			footer={footer}
			width={640}
			className="element-access"
			destroyOnHidden
		>
			<p className="form-panel__hint element-access__intro">
				{group ? (
					<>
						Choose what each group of people can do with {label} in {groupLabel} on {cardName ? `${cardName} items` : 'this card’s items'} in this collection.
						Anyone without a level here follows {groupLabel}. A column can only narrow what {groupLabel} gives: it never shows or changes more.
					</>
				) : (
					<>
						Choose what each group of people can do with {label} on {cardName ? `${cardName} items` : 'this card’s items'} in this collection.
						Anyone without a level here follows the card. These levels only narrow what people already have.
					</>
				)}
			</p>

			{governing && !governing.own ? (
				<Alert
					type="info"
					showIcon
					className="element-access__above"
					message={`The rule on ${aboveName} applies here now. Saving gives ${cardName || 'this card'} its own rule, which replaces it for ${cardName || 'this card'}’s items.`}
				/>
			) : null}

			{sub && aboveLevels ? (
				<Alert
					type="info"
					showIcon
					className="element-access__above element-access__inherited"
					message={`${AboveRule} for ${label} applies here too. A rule saved here can give each group of people at most what it gives, shown on each row below.`}
					description={(
						<>
							<ul className="element-access__inherited-list" aria-label={`What ${aboveRule} gives the rest`}>
								<li>{SHARE_LABEL}: {LEVEL_LABELS[aboveLevels.share] || LEVEL_LABELS.hidden}</li>
								<li>{KEYS_LABEL}: {aboveLevels.keeprOnly ? 'Hidden (only in keepr)' : 'The same as the people using them'}</li>
							</ul>
							<Button size="small" className="element-access__inherited-start" onClick={startFromAbove} disabled={saving}>
								{`Start from ${aboveRule}`}
							</Button>
						</>
					)}
				/>
			) : null}
			{sub && fromAbove?.unknown ? (
				<Alert
					type="warning"
					showIcon
					className="element-access__above element-access__inherited"
					message={`keepr can’t work out what ${aboveRule} for ${label} allows here: it names too many groups. A rule saved here won’t save until a manager of ${parentName} simplifies it.`}
				/>
			) : null}
			{sub && aboveKnown && !fromAbove ? (
				<Alert
					type="info"
					showIcon
					className="element-access__above"
					message={`${parentOf(collection)?.name || 'The collection above'} has no rule for ${label}, so a rule here can give anything the card gives.`}
				/>
			) : null}
			{sub && !aboveKnown ? (
				<Alert
					type="info"
					showIcon
					className="element-access__above"
					message={`${parentName} can set its own rule for ${label}, and it applies here too. A rule saved here replaces it for this collection’s items, even one with nothing chosen.`}
				/>
			) : null}

			{conflicts ? (
				<Alert
					type="error"
					showIcon
					className="element-access__conflicts"
					message="Nothing was saved. These would still show the value to people it would be hidden from:"
					description={(
						<ul className="element-access__conflict-list">
							{conflicts.map((conflict, index) => {
								const { lead, name, tail } = conflictParts(conflict);
								const to = conflictLink(conflict, collectionId);
								const nameNode = name ? (to ? <Link to={to}>{name}</Link> : <span>{name}</span>) : null;
								return (
									<li key={`${conflict?.kind || 'setup'}-${conflict?.id || ''}-${conflict?.where || ''}-${index}`}>
										{lead}{nameNode ? <> {nameNode}</> : null}{tail}.
										{!name && to ? <> <Link to={to}>Open it</Link></> : null}
									</li>
								);
							})}
						</ul>
					)}
				/>
			) : null}
			{error ? <Alert type="error" showIcon className="element-access__error" message={error} /> : null}

			<ul className="element-access__rows" aria-label={`Who can see or change ${subject}`}>
				{rows.map(row => (
					<AccessRow
						key={row.key}
						row={row}
						rows={rows}
						disabled={saving}
						onLevel={level => setLevel(row.key, level)}
						onClear={() => clearRow(row)}
						above={aboveLevels && row.fixed ? aboveLevelFor(aboveLevels, row.audience) : null}
						aboveWords={AboveRule}
						groupLevel={groupLevels && row.fixed ? groupLevelFor(groupLevels, row.audience, rows) : null}
						groupLabel={groupLabel}
					/>
				))}
			</ul>

			{adding ? (
				<div className="element-access__add">
					<Select
						className="element-access__add-group"
						placeholder={groupOptions.length ? 'Pick a group' : 'No groups to add'}
						options={groupOptions.map(({ value, label: text }) => ({ value, label: text }))}
						onChange={addGroup}
						disabled={!groupOptions.length}
						showSearch
						optionFilterProp="label"
						// A collection's groups are a short list: no virtual
						// scrolling, so every one is in the page (and findable).
						virtual={false}
						aria-label="Add a group"
					/>
					<UserSearchSelect
						className="element-access__add-person"
						onChange={addUser}
						excludeIds={takenUserIds}
						placeholder="Search for a person, or type a full email address"
					/>
					<Button onClick={() => setAdding(false)}>Done</Button>
				</div>
			) : (
				<Button type="link" icon={<PLUS />} className="element-access__add-button" onClick={() => setAdding(true)} disabled={saving}>
					Add a person or group
				</Button>
			)}

			<ToggleRow
				className="element-access__keepr-only"
				id="element-access-keepr-only"
				label="Only in keepr"
				description={aboveLevels?.keeprOnly && !keeprOnly
					? `Assistants, apps and API keys never read ${label}, yours included. ${AboveRule} keeps it from them, so a rule here must too.`
					: `Assistants, apps and API keys never read ${label}, yours included. People still see it as set above.`}
				checked={keeprOnly}
				onChange={setKeeprOnly}
				disabled={saving}
			/>

			{missingCount ? (
				<p className="form-panel__hint element-access__missing">
					{missingCount === 1 ? 'One group or person here no longer exists.' : `${missingCount} groups or people here no longer exist.`} They are left out when you save.
				</p>
			) : null}
		</Modal>
	);
}

/** One audience: who, the three-way level, clear, and what it comes to. */
function AccessRow({ row, rows, disabled, onLevel, onClear, above = null, aboveWords = '', groupLevel = null, groupLabel = null }) {
	const audience = row.audience || {};
	const missing = isMissingAudience(audience);
	const isManage = audience.type === 'role' && audience.role === 'manage';
	// Rules only narrow: a viewer or a follower never changes an item, so
	// Edit is not offered to them (a stored one still reads as See only).
	const noEdit = (audience.type === 'role' && audience.role === 'read') || audience.type === 'followers';
	const who = audienceLabel(audience);
	const options = LEVELS.map(level => {
		const blocked = (isManage && level === 'hidden') || (noEdit && level === 'edit' && row.level !== 'edit');
		return {
			value: level,
			label: LEVEL_LABELS[level],
			disabled: disabled || missing || blocked,
			title: isManage && level === 'hidden'
				? 'Managers always see every element: someone has to be able to set it up.'
				: (noEdit && level === 'edit' ? 'These people can only view items.' : undefined),
		};
	});
	const clearLabel = row.fixed ? `Follow the card for ${plainLabel(audience)}` : `Remove ${plainLabel(audience)}`;
	return (
		<li className={`element-access__row${missing ? ' element-access__row--missing' : ''}`} data-audience={row.key}>
			<div className="element-access__who">
				{who}
				{audience.type === 'group' ? <Tag className="element-access__kind">{audience.kind === 'dynamic' ? 'Group built from items' : 'Group'}</Tag> : null}
				{missing ? <Tag className="element-access__kind">No longer exists</Tag> : null}
			</div>
			<div className="element-access__control">
				<Radio.Group
					optionType="button"
					buttonStyle="solid"
					size="small"
					value={row.level || null}
					onChange={event => onLevel(event.target.value)}
					aria-label={`Access for ${plainLabel(audience)}`}
				>
					{options.map(option => (
						<Radio.Button key={option.value} value={option.value} disabled={option.disabled} title={option.title}>
							{option.label}
						</Radio.Button>
					))}
				</Radio.Group>
				{(row.level || !row.fixed) ? (
					<Tooltip title={clearLabel}>
						<Button
							type="text"
							size="small"
							icon={<CLOSE />}
							aria-label={clearLabel}
							onClick={onClear}
							disabled={disabled}
						/>
					</Tooltip>
				) : null}
			</div>
			<Text type="secondary" className="element-access__effective">
				{groupLevel && !row.level ? `Follows ${groupLabel}: ${levelWords(groupLevel).toLowerCase()}` : effectiveWords(row, rows)}
			</Text>
			{groupLevel && row.level ? (
				<Text
					type={exceedsAbove(row, groupLevel, rows) ? 'warning' : 'secondary'}
					className="element-access__group-level"
					data-group-level={groupLevel}
				>
					{`${groupLabel}: ${LEVEL_LABELS[groupLevel]}`}
					{exceedsAbove(row, groupLevel, rows) ? ' · A column never gives more than its group, so this row gets that.' : ''}
				</Text>
			) : null}
			{above ? (
				<Text
					type={exceedsAbove(row, above, rows) ? 'warning' : 'secondary'}
					className="element-access__above-level"
					data-above={above}
				>
					{`${aboveWords}: ${LEVEL_LABELS[above]}`}
					{exceedsAbove(row, above, rows) ? ' · This row gives more, so it won’t save.' : ''}
				</Text>
			) : null}
		</li>
	);
}

function plainLabel(audience) {
	if (audience?.type === 'role') return ROLE_LABELS[audience.role] || audience.role;
	if (audience?.type === 'followers') return FOLLOWERS_LABEL;
	if (audience?.type === 'public') return PUBLIC_LABEL;
	if (audience?.type === 'group') return audience.name || 'this group';
	if (audience?.type === 'user') return (audience.user ? userLabel(audience.user) : '') || 'this person';
	return 'these people';
}

function audienceLabel(audience) {
	if (audience?.type === 'user') {
		if (isMissingAudience(audience)) return <span className="element-access__name">A person who has left</span>;
		return <UserRef user={audience.user || undefined} userId={audience.id} size={20} />;
	}
	if (audience?.type === 'group' && isMissingAudience(audience)) {
		return <span className="element-access__name">A deleted group</span>;
	}
	const hint = audience?.type === 'followers'
		? 'Anyone reading this collection because it is published, or through a link'
		: audience?.type === 'public'
			? 'People filling in a public form, without an account'
			: null;
	return (
		<span className="element-access__name">
			{plainLabel(audience)}
			{hint ? <span className="element-access__hint">{hint}</span> : null}
		</span>
	);
}
